import type { ColumnDef } from '@tanstack/react-table';
import { Plus, ScrollText } from 'lucide-react';
import { useDeferredValue, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuthStore } from '@/auth/store';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { DataTable } from '@/components/ui/data-table';
import { EmptyState } from '@/components/ui/empty-state';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { VesselSelect } from '@/components/ui/vessel-select';
import {
  useCertificatesByCompany,
  useCertificatesByVessel,
  useExpiringCertificates,
} from '@/features/certificates/queries';
import {
  CERTIFICATE_STATUSES,
  statusVisualTone,
  type Certificate,
  type CertificateStatus,
} from '@/features/certificates/types';
import { useEquipment } from '@/features/equipment/queries';
import { useVessels } from '@/features/vessels/queries';
import { vesselDisplayName, type Vessel } from '@/features/vessels/types';
import { ApiError } from '@/lib/api';
import { matchesQuery } from '@/lib/list-search';
import { cn } from '@/lib/utils';

const EXPIRING_WINDOWS = [30, 60, 90] as const;
type ExpiringWindow = (typeof EXPIRING_WINDOWS)[number];

function ListSkeleton() {
  return (
    <Table>
      <TableHeader>
        <TableRow>
          {Array.from({ length: 6 }).map((_, i) => (
            <TableHead key={i}>
              <Skeleton className="h-3 w-20" />
            </TableHead>
          ))}
        </TableRow>
      </TableHeader>
      <TableBody>
        {Array.from({ length: 5 }).map((_, r) => (
          <TableRow key={r}>
            {Array.from({ length: 6 }).map((_, c) => (
              <TableCell key={c}>
                <Skeleton className="h-4 w-full" />
              </TableCell>
            ))}
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
}

function formatDate(iso: string | null | undefined): string {
  if (!iso) return '—';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? iso : d.toLocaleDateString();
}

const STATUS_TONE_CLASS: Record<
  ReturnType<typeof statusVisualTone>,
  string
> = {
  emerald: 'bg-emerald-100 text-emerald-700',
  amber: 'bg-amber-100 text-amber-800',
  red: 'bg-red-100 text-red-700',
  muted: 'bg-slate-100 text-slate-700',
};

function StatusBadge({
  status,
  label,
}: {
  status: CertificateStatus;
  label: string;
}) {
  return (
    <span
      className={cn(
        'inline-flex items-center rounded-md px-2 py-0.5 text-xs font-medium',
        STATUS_TONE_CLASS[statusVisualTone(status)],
      )}
    >
      {label}
    </span>
  );
}

export function CertificatesListPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const user = useAuthStore((s) => s.user);

  const vesselId = searchParams.get('vesselId') ?? undefined;
  const companyIdParam = searchParams.get('companyId') ?? undefined;
  const expiringRaw = searchParams.get('expiringWithin');
  const expiringWithin: ExpiringWindow | undefined = (() => {
    if (!expiringRaw) return undefined;
    const n = Number(expiringRaw);
    return EXPIRING_WINDOWS.includes(n as ExpiringWindow)
      ? (n as ExpiringWindow)
      : undefined;
  })();

  const effectiveCompanyId = companyIdParam ?? user?.companyId ?? undefined;

  // Pick which scope drives the list. The three hooks are always
  // mounted but only one is enabled — the dependency-array contract
  // of useQuery requires a stable hook order across renders.
  const useVesselScope = Boolean(vesselId);
  const useExpiringScope = !useVesselScope && expiringWithin !== undefined;
  const useCompanyScope = !useVesselScope && !useExpiringScope;

  const byVessel = useCertificatesByVessel(useVesselScope ? vesselId : undefined);
  const byCompany = useCertificatesByCompany(
    useCompanyScope ? effectiveCompanyId : undefined,
  );
  const expiring = useExpiringCertificates(
    useExpiringScope ? effectiveCompanyId : undefined,
    expiringWithin,
  );

  const active = useVesselScope ? byVessel : useExpiringScope ? expiring : byCompany;
  const { data, isLoading, isError, error, refetch, isFetching } = active;

  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<CertificateStatus | ''>('');
  const deferredSearch = useDeferredValue(search);

  const filtered = useMemo(() => {
    const list = data ?? [];
    return list
      .filter((c) => (statusFilter === '' ? true : c.status === statusFilter))
      .filter((c) =>
        matchesQuery(c, deferredSearch, [
          (x) => x.certificateNumber,
          (x) => x.title,
          (x) => x.issuerName,
          (x) => x.issuingAuthority,
        ]),
      );
  }, [data, deferredSearch, statusFilter]);

  const { data: vessels } = useVessels();
  // Equipment list is vessel-scoped server-side; without a vessel filter
  // the backend returns the company's full inventory which is enough
  // for tag lookup in the parent column.
  const { data: equipment } = useEquipment(
    vesselId ? { vesselId } : {},
  );

  const vesselsMap = useMemo(() => {
    const m = new Map<string, Vessel>();
    (vessels ?? []).forEach((v) => m.set(v.id, v));
    return m;
  }, [vessels]);

  const equipmentTagById = useMemo(() => {
    const m = new Map<string, string>();
    (equipment ?? []).forEach((e) => m.set(e.id, e.tag));
    return m;
  }, [equipment]);

  const setFilter = (key: string, next: string | null) => {
    const params = new URLSearchParams(searchParams);
    if (next === null || next === '') params.delete(key);
    else params.set(key, next);
    setSearchParams(params, { replace: true });
  };

  const columns = useMemo<ColumnDef<Certificate>[]>(
    () => [
      {
        id: 'identification',
        header: () => t('certificate.columns.identification'),
        cell: ({ row }) => (
          <Link
            to={`/certificates/${row.original.id}`}
            onClick={(e) => e.stopPropagation()}
            className="text-primary hover:underline"
          >
            <span className="font-mono text-sm">
              {row.original.certificateNumber}
            </span>
            <span className="ml-2">{row.original.title}</span>
          </Link>
        ),
      },
      {
        id: 'issuer',
        header: () => t('certificate.columns.issuer'),
        cell: ({ row }) => (
          <div className="flex flex-col">
            <span className="text-sm font-medium">
              {row.original.issuerName}
            </span>
            <span className="text-xs text-muted-foreground">
              {t(`certificate.issuer.${row.original.issuer}`)}
            </span>
          </div>
        ),
      },
      {
        accessorKey: 'issuedAt',
        header: () => t('certificate.columns.issuedAt'),
        cell: ({ getValue }) => formatDate(getValue<string | null>()),
      },
      {
        accessorKey: 'expiresAt',
        header: () => t('certificate.columns.expiresAt'),
        cell: ({ row }) => {
          const isWarn =
            row.original.status === 'expiring_soon' ||
            row.original.status === 'expired';
          return (
            <span
              className={cn(
                'tabular-nums',
                isWarn && 'font-medium text-red-700',
              )}
            >
              {formatDate(row.original.expiresAt)}
            </span>
          );
        },
      },
      {
        id: 'parent',
        header: () => t('certificate.columns.parent'),
        cell: ({ row }) => {
          if (row.original.scope === 'vessel' && row.original.vesselId) {
            const v = vesselsMap.get(row.original.vesselId);
            return (
              <Link
                to={`/vessels/${row.original.vesselId}`}
                onClick={(e) => e.stopPropagation()}
                className="text-primary hover:underline"
              >
                {v ? vesselDisplayName(v) : row.original.vesselId}
              </Link>
            );
          }
          if (row.original.scope === 'equipment' && row.original.equipmentId) {
            const tag =
              equipmentTagById.get(row.original.equipmentId) ??
              row.original.equipmentId;
            return (
              <Link
                to={`/equipment/${row.original.equipmentId}`}
                onClick={(e) => e.stopPropagation()}
                className="font-mono text-xs text-primary hover:underline"
              >
                {tag}
              </Link>
            );
          }
          return '—';
        },
      },
      {
        accessorKey: 'status',
        header: () => t('certificate.columns.status'),
        cell: ({ getValue }) => {
          const s = getValue<CertificateStatus>();
          return <StatusBadge status={s} label={t(`certificate.status.${s}`)} />;
        },
      },
    ],
    [t, vesselsMap, equipmentTagById],
  );

  const errorMessage = (() => {
    if (!isError) return null;
    if (error instanceof ApiError) {
      return t(error.key, {
        defaultValue: t('certificate.list.loadError'),
        ...(error.params ?? {}),
      });
    }
    return t('certificate.list.loadError');
  })();

  // No companyId in auth + no override → backend cannot answer.
  // Surface a hint instead of perpetually loading nothing.
  const missingCompanyContext =
    !useVesselScope && !effectiveCompanyId;

  return (
    <div className="space-y-6">
      <header className="flex flex-wrap items-center justify-between gap-3">
        <h1 className="text-2xl font-semibold">{t('certificate.list.title')}</h1>
        <Button asChild>
          <Link to="/certificates/new">
            <Plus aria-hidden />
            {t('certificate.list.newButton')}
          </Link>
        </Button>
      </header>

      <div className="flex flex-wrap items-end gap-3">
        <label className="flex flex-col gap-1 text-sm">
          <span className="text-muted-foreground">{t('common.search')}</span>
          <Input
            type="search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={t('certificate.list.searchPlaceholder')}
            className="h-9 w-64"
            data-testid="certificates-search"
          />
        </label>
        <label className="flex flex-col gap-1 text-sm">
          <span className="text-muted-foreground">
            {t('certificate.list.filterByVessel')}
          </span>
          <VesselSelect
            value={vesselId ?? null}
            onChange={(v) => setFilter('vesselId', v)}
            allowEmpty
            emptyLabel={t('certificate.list.allVessels')}
            className="h-9 w-56"
          />
        </label>
        <label className="flex flex-col gap-1 text-sm">
          <span className="text-muted-foreground">
            {t('certificate.list.filterByStatus')}
          </span>
          <select
            value={statusFilter}
            onChange={(e) =>
              setStatusFilter(e.target.value as CertificateStatus | '')
            }
            className="flex h-9 w-44 rounded-md border border-input bg-background px-3 py-1 text-sm"
            data-testid="certificates-status-filter"
          >
            <option value="">{t('certificate.list.allStatuses')}</option>
            {CERTIFICATE_STATUSES.map((s) => (
              <option key={s} value={s}>
                {t(`certificate.status.${s}`)}
              </option>
            ))}
          </select>
        </label>
        <label className="flex flex-col gap-1 text-sm">
          <span className="text-muted-foreground">
            {t('certificate.list.filterByExpiring')}
          </span>
          <select
            value={expiringWithin ?? ''}
            onChange={(e) =>
              setFilter('expiringWithin', e.target.value || null)
            }
            className="flex h-9 w-44 rounded-md border border-input bg-background px-3 py-1 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          >
            <option value="">{t('certificate.list.allCertificates')}</option>
            {EXPIRING_WINDOWS.map((d) => (
              <option key={d} value={d}>
                {t('certificate.list.expiringInDays', { count: d })}
              </option>
            ))}
          </select>
        </label>
      </div>

      {missingCompanyContext && (
        <Alert>
          <AlertTitle>{t('certificate.list.noCompanyTitle')}</AlertTitle>
          <AlertDescription>
            {t('certificate.list.noCompanyHint')}
          </AlertDescription>
        </Alert>
      )}

      {errorMessage && (
        <Alert variant="destructive">
          <AlertTitle>{t('certificate.list.loadError')}</AlertTitle>
          <AlertDescription className="space-y-2">
            <p>{errorMessage}</p>
            <Button
              variant="outline"
              size="sm"
              onClick={() => refetch()}
              disabled={isFetching}
            >
              {t('common.retry')}
            </Button>
          </AlertDescription>
        </Alert>
      )}

      {!isError && !missingCompanyContext && (
        <DataTable
          columns={columns}
          data={filtered}
          isLoading={isLoading}
          loadingState={<ListSkeleton />}
          emptyState={
            <EmptyState
              icon={ScrollText}
              title={t('certificate.list.empty')}
              description={t('certificate.list.emptyHint')}
              action={
                <Button asChild size="sm">
                  <Link to="/certificates/new">
                    <Plus aria-hidden />
                    {t('certificate.list.newButton')}
                  </Link>
                </Button>
              }
            />
          }
          onRowClick={(c) => navigate(`/certificates/${c.id}`)}
        />
      )}
    </div>
  );
}
